// src/features/member/components/ProfileEditModal.tsx

import React, {
  useEffect,
  useRef,
  useState,
} from 'react';

import {
  Camera,
  Loader2,
  X,
} from 'lucide-react';

import {
  useDispatch,
  useSelector,
} from 'react-redux';

import type {
  AppDispatch,
  RootState,
} from '../../../app/store';

import {
  setUser,
} from '../../auth/authSlice';

import {
  storeService,
} from '../../store/services/storeService';

import type {
  StoreListItem,
} from '../../store/types/store.types';

import {
  memberService,
} from '../services/memberService';


type ProfileEditModalProps = {
  open: boolean;

  onClose: () => void;

  onUpdated:
    () => Promise<void>;
};


export const ProfileEditModal:
  React.FC<
    ProfileEditModalProps
  > = ({
    open,
    onClose,
    onUpdated,
  }) => {
    const dispatch =
      useDispatch<AppDispatch>();


    const user =
      useSelector(
        (
          state:
            RootState,
        ) =>
          state.auth.user,
      );


    const fileInputRef =
      useRef<HTMLInputElement>(
        null,
      );


    const [
      nickname,
      setNickname,
    ] =
      useState('');


    const [
      storeId,
      setStoreId,
    ] =
      useState(0);


    const [
      stores,
      setStores,
    ] =
      useState<
        StoreListItem[]
      >([]);


    const [
      selectedFile,
      setSelectedFile,
    ] =
      useState<
        File | null
      >(null);


    const [
      imagePreview,
      setImagePreview,
    ] =
      useState<
        string | null
      >(null);


    const [
      isLoadingStores,
      setIsLoadingStores,
    ] =
      useState(false);


    const [
      saving,
      setSaving,
    ] =
      useState(false);


    const [
      errorMessage,
      setErrorMessage,
    ] =
      useState('');


    /**
     * =========================================================
     * 모달 초기화
     * =========================================================
     */
    useEffect(
      () => {
        if (
          !open ||
          !user
        ) {
          return;
        }


        setNickname(
          user.userNickname ??
          '',
        );


        setStoreId(
          user.storeId ??
          0,
        );


        setImagePreview(
          user.profileImageUrl ??
          null,
        );


        setSelectedFile(
          null,
        );


        setErrorMessage(
          '',
        );
      },
      [
        open,
        user,
      ],
    );


    /**
     * =========================================================
     * 매장 목록 조회
     * =========================================================
     */
    useEffect(
      () => {
        if (!open) {
          return;
        }


        let mounted =
          true;


        const loadStores =
          async (): Promise<void> => {
            try {
              setIsLoadingStores(
                true,
              );


              const response =
                await storeService
                  .getStores();


              if (!mounted) {
                return;
              }


              setStores(
                Array.isArray(
                  response,
                )
                  ? response.filter(
                      (
                        store,
                      ) =>
                        store.storeId !==
                        0,
                    )
                  : [],
              );

            } catch (
              error: unknown
            ) {
              if (!mounted) {
                return;
              }


              console.error(
                '프로필 매장 목록 조회 실패:',
                error,
              );


              setStores(
                [],
              );


              setErrorMessage(
                '매장 목록을 불러오지 못했습니다.',
              );

            } finally {
              if (mounted) {
                setIsLoadingStores(
                  false,
                );
              }
            }
          };


        void loadStores();


        return () => {
          mounted =
            false;
        };
      },
      [
        open,
      ],
    );


    /**
     * =========================================================
     * 이미지 선택
     * =========================================================
     */
    const handleImageChange = (
      file:
        File | undefined,
    ): void => {
      if (!file) {
        return;
      }


      if (
        !file.type.startsWith(
          'image/',
        )
      ) {
        setErrorMessage(
          '이미지 파일만 업로드할 수 있습니다.',
        );

        return;
      }


      if (
        file.size >
        5 *
          1024 *
          1024
      ) {
        setErrorMessage(
          '프로필 이미지는 5MB 이하만 업로드할 수 있습니다.',
        );

        return;
      }


      setErrorMessage(
        '',
      );


      setSelectedFile(
        file,
      );


      const reader =
        new FileReader();


      reader.onload = (
        event,
      ) => {
        const result =
          event.target
            ?.result;


        if (
          typeof result ===
          'string'
        ) {
          setImagePreview(
            result,
          );
        }
      };


      reader.readAsDataURL(
        file,
      );
    };


    /**
     * =========================================================
     * Validation
     * =========================================================
     */
    const validate =
      (): string | null => {
        const trimmedNickname =
          nickname.trim();


        if (
          !trimmedNickname
        ) {
          return '닉네임을 입력해주세요.';
        }


        if (
          trimmedNickname.length <
            2 ||
          trimmedNickname.length >
            12
        ) {
          return '닉네임은 2~12자로 입력해주세요.';
        }


        if (
          !Number.isInteger(
            storeId,
          ) ||
          storeId <=
            0
        ) {
          return '소속 매장을 선택해주세요.';
        }


        const storeExists =
          stores.some(
            (
              store,
            ) =>
              store.storeId ===
              storeId,
          );


        if (!storeExists) {
          return '올바른 소속 매장을 선택해주세요.';
        }


        return null;
      };


    /**
     * =========================================================
     * 저장
     * =========================================================
     */
    const handleSave =
      async (): Promise<void> => {
        if (
          saving
        ) {
          return;
        }


        const validationMessage =
          validate();


        if (
          validationMessage
        ) {
          setErrorMessage(
            validationMessage,
          );

          return;
        }


        try {
          setSaving(
            true,
          );


          setErrorMessage(
            '',
          );


          /**
           * 이미지가 변경된 경우만 업로드
           */
          if (
            selectedFile
          ) {
            await memberService
              .uploadProfileImage(
                selectedFile,
              );
          }


          /**
           * 닉네임 / 소속 매장 수정
           */
          await memberService
            .updateProfile({
              userNickname:
                nickname.trim(),

              storeId,
            });


          /**
           * 서버 기준 최신 사용자 재조회
           */
          const updatedUser =
            await memberService
              .getMyInfo();


          dispatch(
            setUser(
              updatedUser,
            ),
          );


          /**
           * Sidebar 포인트 등 재조회
           */
          await onUpdated();

        } catch (
          error: unknown
        ) {
          console.error(
            '프로필 수정 실패:',
            error,
          );


          setErrorMessage(
            error instanceof
            Error
              ? error.message
              : '프로필 수정에 실패했습니다.',
          );

        } finally {
          setSaving(
            false,
          );
        }
      };


    if (!open) {
      return null;
    }


    return (
      <div
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4"
        role="dialog"
        aria-modal="true"
        aria-label="프로필 수정"
        onMouseDown={(
          event,
        ) => {
          if (
            event.target ===
              event.currentTarget &&
            !saving
          ) {
            onClose();
          }
        }}
      >

        <div className="w-full max-w-md overflow-hidden rounded-2xl bg-white shadow-2xl">


          {/* =================================================
              Header
              ================================================= */}
          <header className="flex items-start justify-between border-b border-gray-200 px-5 py-4">

            <div>

              <h2 className="text-lg font-bold text-gray-900">
                프로필 수정
              </h2>


              <p className="mt-1 text-xs leading-5 text-gray-500">
                프로필 이미지, 닉네임과 소속 매장을 변경할 수 있습니다.
              </p>

            </div>


            <button
              type="button"
              onClick={
                onClose
              }
              disabled={
                saving
              }
              className="grid h-9 w-9 shrink-0 place-items-center rounded-lg text-gray-400 transition hover:bg-gray-100 hover:text-gray-700 disabled:cursor-not-allowed disabled:opacity-40"
              aria-label="프로필 수정 닫기"
            >
              <X
                size={20}
              />
            </button>

          </header>


          {/* =================================================
              Body
              ================================================= */}
          <div className="space-y-5 p-5">


            {/* =================================================
                Profile Image
                ================================================= */}
            <div className="flex justify-center">

              <div className="relative">

                <div className="h-24 w-24 overflow-hidden rounded-full border border-gray-200 bg-gray-100">

                  {imagePreview ? (

                    <img
                      src={
                        imagePreview
                      }
                      alt="프로필 미리보기"
                      className="h-full w-full object-cover"
                    />

                  ) : (

                    <div className="grid h-full w-full place-items-center text-gray-400">

                      <Camera
                        size={30}
                      />

                    </div>

                  )}

                </div>


                <button
                  type="button"
                  onClick={() =>
                    fileInputRef
                      .current
                      ?.click()
                  }
                  disabled={
                    saving
                  }
                  className="absolute bottom-0 right-0 grid h-8 w-8 place-items-center rounded-full bg-main text-white shadow-md transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-40"
                  aria-label="프로필 이미지 선택"
                >
                  <Camera
                    size={15}
                  />
                </button>


                <input
                  ref={
                    fileInputRef
                  }
                  type="file"
                  accept="image/*"
                  disabled={
                    saving
                  }
                  className="hidden"
                  onChange={(
                    event,
                  ) => {
                    const file =
                      event.target
                        .files?.[0];


                    handleImageChange(
                      file,
                    );


                    /**
                     * 동일 파일 재선택 가능
                     */
                    event.target.value =
                      '';
                  }}
                />

              </div>

            </div>


            <p className="-mt-2 text-center text-[11px] text-gray-400">
              이미지 파일 · 최대 5MB
            </p>


            {/* =================================================
                Nickname
                ================================================= */}
            <div>

              <label
                htmlFor="profile-edit-nickname"
                className="mb-1.5 block text-sm font-semibold text-gray-700"
              >
                닉네임

                <span className="ml-1 text-red-500">
                  *
                </span>
              </label>


              <input
                id="profile-edit-nickname"
                type="text"
                value={
                  nickname
                }
                maxLength={12}
                disabled={
                  saving
                }
                onChange={(
                  event,
                ) => {
                  setNickname(
                    event.target.value,
                  );


                  setErrorMessage(
                    '',
                  );
                }}
                placeholder="닉네임 2~12자"
                className="h-11 w-full rounded-xl border border-gray-300 bg-white px-3 text-sm text-gray-900 outline-none transition placeholder:text-gray-400 focus:border-main focus:ring-2 focus:ring-main/10 disabled:cursor-not-allowed disabled:bg-gray-100"
              />

            </div>


            {/* =================================================
                Store
                ================================================= */}
            <div>

              <label
                htmlFor="profile-edit-store"
                className="mb-1.5 block text-sm font-semibold text-gray-700"
              >
                소속 매장

                <span className="ml-1 text-red-500">
                  *
                </span>
              </label>


              <div className="relative">

                <select
                  id="profile-edit-store"
                  value={
                    storeId >
                    0
                      ? String(
                          storeId,
                        )
                      : ''
                  }
                  disabled={
                    saving ||
                    isLoadingStores
                  }
                  onChange={(
                    event,
                  ) => {
                    setStoreId(
                      event.target.value
                        ? Number(
                            event.target.value,
                          )
                        : 0,
                    );


                    setErrorMessage(
                      '',
                    );
                  }}
                  className="h-11 w-full appearance-none rounded-xl border border-gray-300 bg-white px-3 pr-9 text-sm text-gray-900 outline-none transition focus:border-main focus:ring-2 focus:ring-main/10 disabled:cursor-not-allowed disabled:bg-gray-100"
                >

                  <option
                    value=""
                    disabled
                  >
                    {isLoadingStores
                      ? '매장 목록을 불러오는 중입니다.'
                      : '소속 매장을 선택해주세요.'}
                  </option>


                  {stores.map(
                    (
                      store,
                    ) => (

                      <option
                        key={
                          store.storeId
                        }
                        value={
                          store.storeId
                        }
                      >
                        {
                          store.storeName
                        }
                      </option>

                    ),
                  )}

                </select>


                <svg
                  className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                  aria-hidden="true"
                >
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M5.23 7.21a.75.75 0 011.06.02L10 11.17l3.71-3.94a.75.75 0 111.09 1.03l-4.25 4.5a.75.75 0 01-1.09 0l-4.25-4.5a.75.75 0 01.02-1.05z"
                  />
                </svg>

              </div>

            </div>


            {/* =================================================
                Error
                ================================================= */}
            {errorMessage && (

              <div
                role="alert"
                className="rounded-xl bg-red-50 px-3 py-2.5 text-sm leading-5 text-red-600"
              >
                {
                  errorMessage
                }
              </div>

            )}

          </div>


          {/* =================================================
              Footer
              ================================================= */}
          <footer className="flex justify-end gap-2 border-t border-gray-200 px-5 py-4">

            <button
              type="button"
              onClick={
                onClose
              }
              disabled={
                saving
              }
              className="h-10 rounded-xl border border-gray-300 px-4 text-sm font-semibold text-gray-700 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
            >
              취소
            </button>


            <button
              type="button"
              onClick={() =>
                void handleSave()
              }
              disabled={
                saving ||
                isLoadingStores
              }
              className="flex h-10 min-w-24 items-center justify-center gap-2 rounded-xl bg-main px-4 text-sm font-semibold text-white transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
            >

              {saving && (

                <Loader2
                  size={15}
                  className="animate-spin"
                />

              )}


              {saving
                ? '저장 중'
                : '저장'}

            </button>

          </footer>

        </div>

      </div>
    );
  };


export default ProfileEditModal;